import React, { useEffect, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Package,
  Truck,
  FilePdf,
  Info,
  ArrowRight,
  CalendarBlank,
} from "@phosphor-icons/react";
import { DateTimePicker } from "@/components/DateTimePicker";

export default function NewAppointment() {
  const nav = useNavigate();
  const [providers, setProviders] = useState([]);
  const [unitTypes, setUnitTypes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const [form, setForm] = useState({
    when: null, // Date object
    provider_id: "",
    unit_type_id: "",
    qty_units: 1,
    qty_pallets: 0,
    qty_pieces: 0,
    notes: "",
  });

  useEffect(() => {
    (async () => {
      const [p, u] = await Promise.all([
        api.get("/catalogs/providers"),
        api.get("/catalogs/unit-types"),
      ]);
      setProviders(p.data);
      setUnitTypes(u.data);
    })();
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.when || !form.provider_id || !form.unit_type_id) {
      toast.error("Completa todos los campos");
      return;
    }
    setBusy(true);
    try {
      const iso = form.when.toISOString();
      const { data } = await api.post("/appointments", {
        appointment_datetime: iso,
        provider_id: form.provider_id,
        unit_type_id: form.unit_type_id,
        qty_units: Number(form.qty_units),
        qty_pallets: Number(form.qty_pallets),
        qty_pieces: Number(form.qty_pieces),
        notes: form.notes,
      });
      setCreated(data);
      toast.success(`Cita creada · Folio ${data.folio}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    if (!created) return;
    try {
      const token = localStorage.getItem("rcn_token");
      const res = await fetch(`${API}/appointments/${created.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("No se pudo generar PDF");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Pase_${created.folio}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (created) {
    return (
      <div className="max-w-2xl mx-auto fade-up">
        <div className="font-mono-data text-[11px] uppercase tracking-[0.3em] text-[#0055FF] mb-2">
          Cita · Creada
        </div>
        <h1 className="font-display text-4xl mb-6">Pase generado</h1>

        <div className="bg-[#111111] border border-white/10 rounded-sm p-8 text-center mb-6">
          <div className="text-[10px] font-mono-data uppercase tracking-widest text-white/50 mb-3">
            Folio asignado
          </div>
          <div
            data-testid="created-folio"
            className="font-mono-data font-bold text-6xl text-[#0055FF] mb-2"
          >
            {created.folio}
          </div>
          <div className="text-sm text-white/50">
            {created.provider_name} · {created.unit_type_name}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <button
            data-testid="download-pdf-btn"
            onClick={downloadPdf}
            className="flex items-center justify-center gap-2 px-5 py-3 rounded-sm bg-[#0055FF] text-white font-semibold text-sm hover:brightness-110 active:scale-[0.98] transition-transform"
          >
            <FilePdf size={18} weight="fill" /> Descargar Pase PDF
          </button>
          <button
            data-testid="goto-detail-btn"
            onClick={() => nav(`/citas/${created.id}`)}
            className="flex items-center justify-center gap-2 px-5 py-3 rounded-sm border border-white/10 text-sm hover:bg-white/5 transition-colors"
          >
            Ir a detalle de recibo <ArrowRight size={16} />
          </button>
        </div>

        <button
          onClick={() => {
            setCreated(null);
            setForm({
              when: null,
              provider_id: "",
              unit_type_id: "",
              qty_units: 1,
              qty_pallets: 0,
              qty_pieces: 0,
              notes: "",
            });
          }}
          className="mt-6 text-sm text-white/50 hover:text-white transition-colors"
        >
          + Crear otra cita
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <div className="font-mono-data text-[11px] uppercase tracking-[0.3em] text-[#0055FF] mb-2">
          Nueva · Cita
        </div>
        <h1 className="font-display text-4xl sm:text-5xl">Programar Recepción</h1>
        <p className="text-white/50 text-sm mt-2">
          El folio se asignará automáticamente al guardar.
        </p>
      </div>

      <form onSubmit={submit} className="max-w-4xl">
        <div className="bg-[#111111] border border-white/10 rounded-sm p-6">
          <SectionTitle icon={CalendarBlank} title="Fecha y hora" />
          <div className="mb-6">
            <DateTimePicker
              value={form.when}
              onChange={(v) => update("when", v)}
              testIdDate="input-date"
              testIdTime="input-time"
            />
          </div>

          <SectionTitle icon={Truck} title="Proveedor y unidad" />
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <SelectField
              label="Proveedor"
              value={form.provider_id}
              onChange={(v) => update("provider_id", v)}
              options={providers}
              testId="select-provider"
            />
            <SelectField
              label="Tipo de unidad"
              value={form.unit_type_id}
              onChange={(v) => update("unit_type_id", v)}
              options={unitTypes}
              testId="select-unit"
            />
          </div>

          <SectionTitle icon={Package} title="Cantidades esperadas" />
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <Input
              label="Unidades"
              type="number"
              value={form.qty_units}
              onChange={(v) => update("qty_units", v)}
              testId="input-qty-units"
              min={0}
            />
            <Input
              label="Tarimas"
              type="number"
              value={form.qty_pallets}
              onChange={(v) => update("qty_pallets", v)}
              testId="input-qty-pallets"
              min={0}
            />
            <Input
              label="Piezas"
              type="number"
              value={form.qty_pieces}
              onChange={(v) => update("qty_pieces", v)}
              testId="input-qty-pieces"
              min={0}
            />
          </div>

          <SectionTitle icon={Info} title="Notas (opcional)" />
          <textarea
            data-testid="input-notes"
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
            rows={3}
            placeholder="Instrucciones especiales para el andén…"
            className="w-full px-3 py-2.5 rounded-sm bg-[#0A0A0A] border border-white/10 text-sm text-white placeholder:text-white/30 focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] transition-colors outline-none"
          />
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button
            data-testid="submit-appointment"
            disabled={busy}
            className="px-6 py-3 rounded-sm bg-[#0055FF] text-white font-semibold text-sm hover:brightness-110 active:scale-[0.98] transition-transform disabled:opacity-50"
          >
            {busy ? "Guardando…" : "Crear cita y generar folio"}
          </button>
          <button
            type="button"
            onClick={() => nav("/citas")}
            className="px-4 py-3 rounded-sm border border-white/10 text-sm text-white/70 hover:bg-white/5 transition-colors"
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}

function SectionTitle({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon size={16} className="text-[#0055FF]" />
      <div className="font-mono-data text-[10px] uppercase tracking-widest text-white/50">
        {title}
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testId, min }) {
  return (
    <div>
      <label className="block text-[11px] font-mono-data uppercase tracking-widest text-white/50 mb-2">
        {label}
      </label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        min={min}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2.5 rounded-sm bg-[#0A0A0A] border border-white/10 text-sm text-white focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] transition-colors outline-none"
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="block text-[11px] font-mono-data uppercase tracking-widest text-white/50 mb-2">
        {label}
      </label>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2.5 rounded-sm bg-[#0A0A0A] border border-white/10 text-sm text-white focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] transition-colors outline-none"
      >
        <option value="">Selecciona…</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
    </div>
  );
}
